import type { ComponentProps, ReactNode } from "react";
import React from "react";
import { createRoot } from "react-dom/client";
import components from "@/react/index";
import { ToastAlertProvider } from "@procore/toast-alert";
import { GroupContextProvider } from "./providers/group-context-provider";
import { PermissionContextProvider } from "./providers/permission-context-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorComponent } from "./components/ErrorComponent/ErrorComponent";
import { BUGSNAG_META_TAB, buildUserData } from "@/lib/utils/bugsnag-content-helper";
import { authManager } from "@/lib/managers/auth-manager";
import EnvironmentI18n from "./providers/EnvironmentI18n";
import { ThirdPartyScripts, useThirdPartyScriptsContext } from "@procore/third-party-scripts";

type ComponentName = keyof typeof components;

const queryClient = new QueryClient();

/**
 * Error boundary wrapper that uses Bugsnag from @procore/third-party-scripts.
 * Must be rendered inside ThirdPartyScripts provider.
 */
function BugsnagErrorBoundary({ children }: { children: ReactNode }) {
   const { scripts } = useThirdPartyScriptsContext();
   const bugsnagClient = scripts?.bugsnag?.client;

   const handleError = (error: Error) => {
      if (bugsnagClient) {
         bugsnagClient.notify(error, (event) => {
            const user = authManager.authedUser();
            if (user) {
               event.addMetadata(
                  BUGSNAG_META_TAB.USER_DATA,
                  buildUserData(user, authManager.activePermission),
               );
            }
            event.severity = "error";
            event.context = "React Error Boundary";
         });
      }
   };

   return (
      <ErrorBoundary FallbackComponent={ErrorComponent} onError={handleError}>
         {children}
      </ErrorBoundary>
   );
}

export default <TName extends ComponentName>(
   mountId: string,
   componentName: TName,
   props: ComponentProps<(typeof components)[TName]>,
) => {
   const component = components[componentName] ?? components.NotFound;
   const container = document.getElementById(mountId);
   const root = createRoot(container!);

   root.render(
      <QueryClientProvider client={queryClient}>
         <EnvironmentI18n locale={(window.I18n?.locale as any) ?? "en"}>
            <ThirdPartyScripts>
               <BugsnagErrorBoundary>
                  <ToastAlertProvider>
                     <GroupContextProvider>
                        <PermissionContextProvider>
                           <div className="react-container">
                              {React.createElement<any>(component, props)}
                           </div>
                        </PermissionContextProvider>
                     </GroupContextProvider>
                  </ToastAlertProvider>
               </BugsnagErrorBoundary>
            </ThirdPartyScripts>
         </EnvironmentI18n>
      </QueryClientProvider>,
   );
   return root;
};
